import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';
import { useStudents } from 'context/StudentsContext';

// ---------------------------------------------------------------------------
// StudentRoster — the class-list view a teacher opens by tapping
// "Student Profiles" in the sidebar or "View All" on the dashboard.
//
// Reads directly from StudentsContext so it stays in sync with:
//   * Live Supabase reads (postgres_changes subscription)
//   * Demo-mode localStorage writes
//   * Additions from the dashboard's AddStudentModal
//
// Clicking a row navigates to /student-profile-management/<id>, which the
// parent index.jsx switches over to render the single-student profile view.
// ---------------------------------------------------------------------------

const gradeSortKey = (grade) => {
  if (grade === null || grade === undefined || grade === '') return 999;
  if (grade === 'pre-k') return -1;
  if (grade === 'k') return 0;
  const n = Number(grade);
  return Number.isNaN(n) ? 998 : n;
};

const gradeLabel = (grade) => {
  if (grade === null || grade === undefined || grade === '') return '—';
  if (grade === 'pre-k') return 'Pre-K';
  if (grade === 'k') return 'K';
  const n = Number(grade);
  if (!Number.isNaN(n)) return `Grade ${n}`;
  return String(grade);
};

// Compact "days-until-review" pill for the roster row. Overdue / <=30d get
// a strong color so a busy screen still surfaces the urgent ones.
const iepReviewPill = (dueRaw) => {
  if (!dueRaw) return null;
  const due = new Date(dueRaw);
  if (Number.isNaN(due.getTime())) return null;
  const now = new Date();
  const startOfToday = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
  );
  const days = Math.round((due - startOfToday) / (1000 * 60 * 60 * 24));
  if (days < 0) {
    return {
      label: `Review overdue ${Math.abs(days)}d`,
      classes: 'bg-red-50 text-red-700 border-red-200',
    };
  }
  if (days === 0) {
    return {
      label: 'Review due today',
      classes: 'bg-red-50 text-red-700 border-red-200',
    };
  }
  if (days <= 30) {
    return {
      label: `Review in ${days}d`,
      classes: 'bg-amber-50 text-amber-800 border-amber-200',
    };
  }
  return null;
};

const StudentRoster = ({ onAddStudent, initialQuery = '' }) => {
  const navigate = useNavigate();
  const { students, loading, error, isLive, refresh } = useStudents();
  const [query, setQuery] = useState(initialQuery);
  const [sortBy, setSortBy] = useState('name'); // name | grade | recent
  const [statusFilter, setStatusFilter] = useState('active'); // active | archived | all

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let rows = students;

    if (statusFilter === 'active') {
      rows = rows.filter((s) => (s.status || 'active') !== 'archived');
    } else if (statusFilter === 'archived') {
      rows = rows.filter((s) => s.status === 'archived');
    }

    if (q) {
      rows = rows.filter((s) => {
        const name = `${s.firstName ?? ''} ${s.lastName ?? ''}`.toLowerCase();
        return (
          name.includes(q) ||
          (s.id || '').toLowerCase().includes(q) ||
          String(s.grade ?? '').toLowerCase().includes(q) ||
          (s.primaryDisability || '').toLowerCase().includes(q)
        );
      });
    }

    const sorted = [...rows];
    if (sortBy === 'name') {
      sorted.sort((a, b) => {
        const an = `${a.lastName ?? ''} ${a.firstName ?? ''}`.trim().toLowerCase();
        const bn = `${b.lastName ?? ''} ${b.firstName ?? ''}`.trim().toLowerCase();
        return an.localeCompare(bn);
      });
    } else if (sortBy === 'grade') {
      sorted.sort((a, b) => gradeSortKey(a.grade) - gradeSortKey(b.grade));
    } else if (sortBy === 'recent') {
      sorted.sort((a, b) => {
        const at = String(a.lastUpdated || a.createdAt || '');
        const bt = String(b.lastUpdated || b.createdAt || '');
        return bt.localeCompare(at);
      });
    } else if (sortBy === 'review-due') {
      // Nulls sink to the bottom so students with a real due date rise up.
      sorted.sort((a, b) => {
        const av = a.iepAnnualReviewDue || '9999-12-31';
        const bv = b.iepAnnualReviewDue || '9999-12-31';
        return String(av).localeCompare(String(bv));
      });
    }
    return sorted;
  }, [students, query, sortBy, statusFilter]);

  const handleOpen = (id) => {
    navigate(`/student-profile-management/${encodeURIComponent(id)}`);
  };

  const totalActive = useMemo(
    () => students.filter((s) => (s.status || 'active') !== 'archived').length,
    [students],
  );

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      {/* Header + controls */}
      <div className="p-4 sm:p-6 border-b border-border">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <div>
            <h2 className="text-xl font-semibold text-foreground">
              Class roster
            </h2>
            <p className="text-sm text-muted-foreground mt-0.5">
              {totalActive} active student{totalActive === 1 ? '' : 's'}
              {isLive ? (
                <span className="ml-2 inline-flex items-center gap-1 text-xs text-success">
                  <span className="w-1.5 h-1.5 rounded-full bg-success" />
                  Live sync
                </span>
              ) : (
                <span className="ml-2 inline-flex items-center gap-1 text-xs text-warning">
                  <span className="w-1.5 h-1.5 rounded-full bg-warning" />
                  Demo mode (this browser only)
                </span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              variant="outline"
              size="sm"
              onClick={() => refresh()}
              iconName="RefreshCw"
              iconPosition="left"
              disabled={loading}
              title="Reload from server"
            >
              Refresh
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/students/import')}
              iconName="Upload"
              iconPosition="left"
              title="Bulk import your caseload from a spreadsheet (PowerSchool, EdPlan, etc.)"
            >
              Import from CSV
            </Button>
            {onAddStudent && (
              <Button
                variant="default"
                size="sm"
                onClick={onAddStudent}
                iconName="Plus"
                iconPosition="left"
              >
                Add student
              </Button>
            )}
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-2">
          <div className="relative flex-1">
            <Icon
              name="Search"
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
            />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name, grade, ID, or disability"
              className="w-full pl-9 pr-3 py-2 text-sm bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
          </div>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="text-sm bg-background border border-border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/40"
          >
            <option value="name">Sort: Last name (A–Z)</option>
            <option value="grade">Sort: Grade level</option>
            <option value="recent">Sort: Recently updated</option>
            <option value="review-due">Sort: IEP review due</option>
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="text-sm bg-background border border-border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/40"
          >
            <option value="active">Show: Active</option>
            <option value="archived">Show: Archived</option>
            <option value="all">Show: All (incl. archived)</option>
          </select>
        </div>
      </div>

      {/* Body */}
      {error ? (
        <div className="p-6">
          <div className="p-3 rounded-md bg-destructive/10 border border-destructive/30">
            <div className="flex items-start gap-2">
              <Icon
                name="AlertCircle"
                size={16}
                className="text-destructive flex-shrink-0 mt-0.5"
              />
              <div className="text-sm text-foreground">
                <p className="font-medium">Couldn't load students</p>
                <p className="text-muted-foreground mt-1">{error}</p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => refresh()}
                  className="mt-2"
                >
                  Try again
                </Button>
              </div>
            </div>
          </div>
        </div>
      ) : loading && students.length === 0 ? (
        <div className="py-16 text-center text-sm text-muted-foreground">
          <Icon
            name="Loader2"
            size={20}
            className="mx-auto mb-2 animate-spin text-muted-foreground"
          />
          Loading students…
        </div>
      ) : students.length === 0 ? (
        <div className="py-16 px-6 text-center">
          <Icon
            name="Users"
            size={40}
            className="text-muted-foreground mx-auto mb-3"
          />
          <h3 className="text-base font-medium text-foreground">
            No students yet
          </h3>
          <p className="text-sm text-muted-foreground mt-1 max-w-sm mx-auto">
            {isLive
              ? 'Add your first student to build your class roster. Their profile, IEP goals, and behavior data will live here.'
              : 'Demo mode: any student you add will be stored in this browser only. Connect Supabase to sync across devices.'}
          </p>
          <div className="mt-4 flex items-center justify-center gap-2 flex-wrap">
            {onAddStudent && (
              <Button
                variant="default"
                onClick={onAddStudent}
                iconName="Plus"
                iconPosition="left"
              >
                Add your first student
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => navigate('/students/import')}
              iconName="Upload"
              iconPosition="left"
              title="Import your whole caseload from a spreadsheet"
            >
              Or import from a spreadsheet
            </Button>
          </div>
        </div>
      ) : filtered.length === 0 ? (
        <div className="py-12 px-6 text-center">
          <Icon
            name="SearchX"
            size={32}
            className="text-muted-foreground mx-auto mb-2"
          />
          <p className="text-sm font-medium text-foreground">
            No students match &ldquo;{query}&rdquo;
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            Try a different search, or clear filters.
          </p>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setQuery('');
              setStatusFilter('active');
            }}
            className="mt-3"
          >
            Clear search & filters
          </Button>
        </div>
      ) : (
        <ul className="divide-y divide-border">
          {filtered.map((student) => {
            const initials = `${(student.firstName?.[0] || '?').toUpperCase()}${(student.lastName?.[0] || '').toUpperCase()}`;
            const archived = student.status === 'archived';
            const attention = student.status === 'needs-attention';
            const reviewPill = archived
              ? null
              : iepReviewPill(student.iepAnnualReviewDue);
            return (
              <li key={student.id}>
                <button
                  type="button"
                  onClick={() => handleOpen(student.id)}
                  className="w-full flex items-center gap-3 sm:gap-4 px-4 sm:px-6 py-3 sm:py-4 hover:bg-muted/50 focus:bg-muted focus:outline-none transition-colors text-left"
                >
                  <div className="relative flex-shrink-0">
                    {student.photo ? (
                      <Image
                        src={student.photo}
                        alt={`${student.firstName || ''} ${student.lastName || ''}`.trim()}
                        className="w-10 h-10 sm:w-11 sm:h-11 rounded-full object-cover border border-border"
                      />
                    ) : (
                      <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-full bg-primary/10 text-primary text-sm font-semibold flex items-center justify-center border border-border">
                        {initials}
                      </div>
                    )}
                    {attention && (
                      <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-warning border-2 border-card" title="Needs attention" />
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-medium text-foreground truncate">
                        {student.firstName || 'Unnamed'} {student.lastName || ''}
                      </p>
                      {archived && (
                        <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-muted text-muted-foreground border border-border">
                          Archived
                        </span>
                      )}
                      {reviewPill && (
                        <span
                          className={`inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full border ${reviewPill.classes}`}
                        >
                          <Icon name="CalendarClock" size={10} />
                          {reviewPill.label}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5 truncate">
                      {gradeLabel(student.grade)}
                      {student.primaryDisability ? (
                        <>
                          <span className="mx-1.5">·</span>
                          <span className="capitalize">
                            {String(student.primaryDisability).replace(/[-_]/g, ' ')}
                          </span>
                        </>
                      ) : null}
                      <span className="mx-1.5">·</span>
                      <span className="font-mono text-[11px]">{student.id}</span>
                    </p>
                  </div>

                  <Icon
                    name="ChevronRight"
                    size={16}
                    className="text-muted-foreground flex-shrink-0"
                  />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default StudentRoster;
